import type { FallbackProps } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui'

export function ApiErrorDisplay({ error, resetErrorBoundary }: Readonly<FallbackProps>) {
  const { t } = useTranslation('common')
  return (
    <div className="border-muted flex w-full items-center justify-center rounded-none border">
      <Card className="w-full max-w-2xl border-none">
        <CardHeader className="pb-4 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
            <Icon name="circle-close" className="h-10 w-10 text-red-800" />
          </div>
          <CardTitle className="text-2xl font-semibold text-neutral-700">
            {t('components.api_error_display.titulo')}
          </CardTitle>
          <CardDescription className="mt-2 text-base text-neutral-800">
            {t('components.api_error_display.subtitulo')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="flex flex-col items-center justify-center gap-6">
            <div>
              <Render.If condition={resetErrorBoundary}>
                {(retry) => (
                  <Button onClick={retry} className="flex items-center gap-2">
                    {t('components.api_error_display.tente_novamente')}
                  </Button>
                )}
              </Render.If>
            </div>
            <span className="text-neutral-600">{t('components.api_error_display.descricao')}</span>
          </div>
          <Collapsible>
            <CollapsibleTrigger asChild className="group">
              <Button variant="ghost" className="h-7 hover:bg-sky-50 hover:text-sky-800">
                <Icon name="chevron-down" className="group-data-[state=open]:rotate-180" />
                {t('components.api_error_display.ver_detalhes')}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="space-y-3">
                  <Render.If condition={error?.status}>
                    {(status) => (
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-600">HTTP STATUS</div>
                        <code className="rounded font-mono text-sm text-orange-700">{status}</code>
                      </div>
                    )}
                  </Render.If>
                  <Render.If condition={error?.config?.url}>
                    {(url) => (
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-600">
                          {t('components.api_error_display.request_url')}
                        </div>
                        <code className="rounded font-mono text-sm break-all text-blue-700">{url}</code>
                      </div>
                    )}
                  </Render.If>
                  <div>
                    <div className="mb-1 text-xs font-medium text-gray-600">
                      {t('components.api_error_display.error_message')}
                    </div>
                    <code className="rounded font-mono text-sm break-all text-red-700">{error?.message}</code>
                  </div>
                  <Render.If condition={error?.response}>
                    {(response) => (
                      <div>
                        <div className="mb-1 text-xs font-medium text-gray-600">
                          {t('components.api_error_display.response')}
                        </div>
                        <pre className="overflow-x-auto rounded border bg-gray-100 p-3 font-mono text-xs whitespace-pre-wrap text-gray-700">
                          {JSON.stringify(response, null, 2)}
                        </pre>
                      </div>
                    )}
                  </Render.If>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>
    </div>
  )
}
